import React, { useState, ReactNode } from 'react';
import classnames from 'classnames';
import { ChipProps } from './Chip.interface';
import { ReactComponent as EditIcon } from 'app/assets/Icons/Chips/Edit.svg';
import { ReactComponent as MountainIcon } from 'app/assets/Icons/Chips/Mountain.svg';
import { ReactComponent as SuitcaseIcon } from 'app/assets/Icons/Chips/Suitcase.svg';
import { ReactComponent as HeartIcon } from 'app/assets/Icons/Chips/Heart.svg';
import { ReactComponent as PackageIcon } from 'app/assets/Icons/Chips/Package.svg';
import { ReactComponent as LightningIcon } from 'app/assets/Icons/Chips/Lightning.svg';
import { ReactComponent as GraduationIcon } from 'app/assets/Icons/Chips/Graduation.svg';
import { ReactComponent as GlobeIcon } from 'app/assets/Icons/Chips/Globe.svg';
import { ReactComponent as BalanceIcon } from 'app/assets/Icons/Chips/Balance.svg';
import { ReactComponent as CloudIcon } from 'app/assets/Icons/Chips/Cloud.svg';
import { ReactComponent as CoinIcon } from 'app/assets/Icons/Chips/Coin.svg';
import { ReactComponent as DiagramIcon } from 'app/assets/Icons/Chips/Diagram.svg';
import { ReactComponent as GearIcon } from 'app/assets/Icons/Chips/Gear.svg';
import { ReactComponent as LocationIcon } from 'app/assets/Icons/Chips/Location.svg';
import { ReactComponent as MailIcon } from 'app/assets/Icons/Chips/Mail.svg';
import { ReactComponent as MedicalIcon } from 'app/assets/Icons/Chips/Medical.svg';
import { ReactComponent as PhoneIcon } from 'app/assets/Icons/Chips/Phone.svg';
import { ReactComponent as TagIcon } from 'app/assets/Icons/Chips/Tag.svg';
import { ReactComponent as TargetIcon } from 'app/assets/Icons/Chips/Target.svg';
import { ReactComponent as CheckIcon } from 'app/assets/Icons/Check.svg';
import { ReactComponent as SoundIcon } from 'app/assets/Icons/Chips/Sound.svg';
import { ReactComponent as CrossArrowIcon } from 'app/assets/Icons/Chips/Cross.svg';
import { ReactComponent as TrashIcon } from 'app/assets/Icons/Delete.svg';
import { ReactComponent as DownloadIcon } from 'app/assets/Icons/Download.svg';
import { ReactComponent as PlayIcon } from 'app/assets/Icons/Play.svg';
import { ReactComponent as CardioIcon } from 'app/assets/Icons/Cardio.svg';
import { ReactComponent as ArrowsIcon } from 'app/assets/Icons/Arrows.svg';
import styles from './Chip.module.scss';

const IconMapping: { [key: string]: ReactNode } = {
  Mountain: <MountainIcon />,
  Suitecase: <SuitcaseIcon />,
  Package: <PackageIcon />,
  Edit: <EditIcon />,
  Heart: <HeartIcon />,
  Lightning: <LightningIcon />,
  Graduation: <GraduationIcon />,
  Globe: <GlobeIcon />,
  Balance: <BalanceIcon />,
  Cloud: <CloudIcon />,
  Coin: <CoinIcon />,
  Diagram: <DiagramIcon />,
  Gear: <GearIcon />,
  Location: <LocationIcon />,
  Mail: <MailIcon />,
  Medical: <MedicalIcon />,
  Tag: <TagIcon />,
  Phone: <PhoneIcon />,
  Target: <TargetIcon />,
  Check: <CheckIcon />,
  Sound: <SoundIcon />,
  CrossArrow: <CrossArrowIcon />,
  Play: <PlayIcon />,
  Download: <DownloadIcon />,
  Trash: <TrashIcon />,
  Life: <CardioIcon />,
  Arrows: <ArrowsIcon />
};

const MaturityMapping: { [key: string]: string } = {
  Low: 'var(--color-gradiant-blue-20)',
  Medium: 'var(--color-gradiant-blue-40)',
  High: 'var(--color-gradiant-blue-60)'
};

export const Chip: React.FC<ChipProps> = ({
  icon,
  maturity,
  value,
  id,
  className = '',
  selected,
  disabled = false,
  onClick
}) => {
  const [status, setStatus] = useState<boolean>(selected ?? false);
  const handleClick = (): void => {
    setStatus((prev) => !prev);
    onClick?.({
      status: selected === undefined ? !status : !selected,
      value: id
    });
  };

  const borderStyle = maturity
    ? { border: `2px solid ${MaturityMapping[maturity]}` }
    : undefined;

  return (
    <button
      type="button"
      className={classnames(styles.chip, {
        [styles.selected]: selected ?? status,
        [styles.disabled]: disabled,
        [className]: className !== undefined
      })}
      onClick={handleClick}
      data-testid="chip"
      style={borderStyle}
      disabled={disabled}
      aria-pressed={selected ?? status}
    >
      {icon !== undefined && IconMapping[icon]} {value}
    </button>
  );
};
